import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import React from 'react';
import { useController } from 'react-hook-form';

interface SelectInputProps<T, D> {
  name: string;
  label: string;
  rules?: Object;
  defaultValue?: D;
  fullWidth?: boolean;
  multiple?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  options: T[];
  getOptionLabel(option: T): string;
  getOptionSelected?(option: T, value: T): boolean;
  getOptionDisabled?(option: T): boolean;
  loading?: boolean;
  disableCloseOnSelect?: boolean;
  disableClearable?: boolean;
  filterSelectedOptions?: boolean;
  noOptionsText?: string;
}

export function SelectInput<T, D>({
  name,
  label,
  defaultValue,
  rules,
  options,
  fullWidth,
  loading,
  multiple,
  disabled,
  disableCloseOnSelect,
  disableClearable,
  filterSelectedOptions,
  noOptionsText,
  getOptionLabel,
  getOptionSelected,
  getOptionDisabled,
}: SelectInputProps<T, D>) {
  const {
    field: { ref, value, onChange },
    fieldState: { error },
  } = useController({
    name,
    defaultValue,
    rules,
  });
  return (
    <Autocomplete
      ref={ref}
      value={value}
      id={name}
      onChange={(_, value) => {
        onChange(value);
      }}
      multiple={multiple}
      disabled={disabled}
      disableCloseOnSelect={disableCloseOnSelect}
      options={options}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={getOptionSelected}
      getOptionDisabled={getOptionDisabled}
      noOptionsText={noOptionsText}
      disableClearable={disableClearable}
      filterSelectedOptions={filterSelectedOptions}
      fullWidth={fullWidth}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          error={Boolean(error)}
          helperText={error?.message}
          InputProps={{
            ...params.InputProps,
            endAdornment: loading ? <CircularProgress color='inherit' size={20} /> : params.InputProps.endAdornment,
          }}
        />
      )}
    />
  );
}
